import React, { ComponentType, ReactNode, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { UxContext } from '@client/context/UxContext';
import { useGlobalStore } from '@client/store/globalStore/context/globalContext';
import { UserData } from '@client/store/globalStore/state/initialGlobalState';
import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import AddBoxIcon from '@mui/icons-material/AddBox';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Container, Grid } from '@mui/material';
import { styled } from '@mui/system';
import './Assets/itemList.scss';

import { If } from './If';
import ItemListAutocomplete, { ListItem } from './ItemListAutocomplete';
import RespButton from './RespButton';
import TitleAndDescription from './TitleAndDescription';

const GridButtonContainer = styled(Grid)(({ theme }) => ({
    position: 'sticky',
    top: '64px',
    background: theme.palette.background.default,
    zIndex: 1000,
    marginBottom: '10px'
}));

interface BaseItem {
    id: string;
    name: string;
    locked?: unknown;
    linked?: unknown;
}

type EnrichedItem<T> = T & {
    lockedReason?: string | false;
    linkedReason?: string | false;
    optionChart?: unknown;
    additionalItemListInfo?: ReactNode;
};

export interface ListComponentProps<T> {
    itemList: T[];
    onChange: (item: T | null) => void;
    onView: (item: T) => void;
    onEdit: (item: T) => void;
    onDelete: (item: T) => void;
    onDuplicate: (item: T) => void;
    allowDuplicate?: boolean;
    userData: UserData;
}

interface ItemListProps<T extends BaseItem> {
    chart?: (item: T) => unknown;
    isLocked?: (item: T) => string | false;
    isLinked?: (item: T) => string | false;
    additionalItemListInfo?: (item: T) => ReactNode;
    allowDuplicate?: boolean;
    showEditButton?: boolean;
    ListComponent?: ComponentType<ListComponentProps<EnrichedItem<T>>>;
}

interface DuplicateResponse {
    id?: string;
    success?: string;
    error?: string;
}

function ItemList<T extends BaseItem>({ chart, isLocked, isLinked, additionalItemListInfo, allowDuplicate, showEditButton, ListComponent }: ItemListProps<T>) {
    const { handleSnackbar, handleAlert } = React.useContext(UxContext)!;
    const {
        globalState: { userData }
    } = useGlobalStore();
    const {
        pageState: { pageConfig },
        pageActions
    } = usePageStore();
    const history = useHistory();

    const [selectedItem, setSelectedItem] = React.useState<EnrichedItem<T> | null>(null);
    const [itemList, setItemList] = React.useState<EnrichedItem<T>[] | null>(null);

    const getItems = useCallback(async (): Promise<void> => {
        if (!pageConfig.fetchEndPoint) {
            return;
        }

        // there is no form in the main menu
        pageActions.formData.overrideFormData({});

        const rawData = await lediumApi.get<T[]>(`${pageConfig.fetchEndPoint}/`);
        if (!rawData) {
            setItemList(null);
            return;
        }

        let data: EnrichedItem<T>[] = rawData as EnrichedItem<T>[];
        if (isLocked) {
            data = data.map((item) => ({ ...item, lockedReason: isLocked(item) }));
        }
        if (isLinked) {
            data = data.map((item) => ({ ...item, linkedReason: isLinked(item) }));
        }
        if (chart) {
            data = data.map((item) => ({ ...item, optionChart: chart(item) }));
        }
        if (additionalItemListInfo) {
            data = data.map((item) => ({ ...item, additionalItemListInfo: additionalItemListInfo(item) }));
        }
        setItemList(data);
    }, [pageConfig]);

    React.useEffect(() => {
        getItems();
        pageActions.api.setLoading(true);
    }, [pageConfig]);

    React.useEffect(() => {
        if (itemList !== null) {
            pageActions.api.setLoading(false);
        }
    }, [itemList]);

    function onClickAddNew() {
        history.push(`${pageConfig.url}/new`);
    }

    function onClickEdit(item: EnrichedItem<T>): void {
        history.push(`${pageConfig.url}/edit/${item.id}`);
    }

    function onClickView(item: EnrichedItem<T>): void {
        history.push(`${pageConfig.url}/view/${item.id}`);
    }

    function onClickDelete(item: EnrichedItem<T>): void {
        handleAlert({
            title: i18n('are_you_sure') as string,
            message: <span>{i18n('do_you_want_to_delete_X', <b>{item.name}</b>)}</span>,
            okCallback: () => {
                lediumApi.delete(`${pageConfig.apiEndPoint}/${item.id}`).then(getItems);
            }
        });
    }

    function onClickDuplicate(item: EnrichedItem<T>): void {
        handleAlert({
            title: i18n('are_you_sure') as string,
            message: <span>{i18n('do_you_want_to_duplicate_X', <b>{item.name}</b>)}</span>,
            okCallback: async () => {
                const data = await lediumApi.get<DuplicateResponse>(`${pageConfig.apiEndPoint}/duplicate/${item.id}`);
                if (data?.id) {
                    handleSnackbar({
                        message: data.success || (i18n('X_is_successfully_duplicated', pageConfig.itemName) as string)
                    });
                    getItems();
                } else {
                    handleSnackbar({
                        message: data?.error || (i18n('something_went_wrong') as string),
                        severity: 'error'
                    });
                }
            }
        });
    }

    return (
        <Container>
            <Grid container spacing={3}>
                <TitleAndDescription />

                <Grid item xs={12}>
                    <GridButtonContainer container spacing={3}>
                        {userData?.admin ? (
                            <Grid item xs sx={{ textAlign: 'center' }}>
                                <RespButton variant="contained" onClick={onClickAddNew} startIcon={<AddBoxIcon />}>
                                    {i18n('add_new')}
                                </RespButton>
                            </Grid>
                        ) : null}

                        {(!ListComponent || showEditButton) && (
                            <>
                                {!ListComponent && (
                                    <Grid item xs sx={{ textAlign: 'center' }}>
                                        <RespButton
                                            variant="contained"
                                            color="primary"
                                            disabled={!selectedItem}
                                            onClick={() => selectedItem && onClickView(selectedItem)}
                                            startIcon={<VisibilityIcon />}
                                        >
                                            {i18n('view')}
                                        </RespButton>
                                    </Grid>
                                )}

                                <If condition={!!userData?.admin}>
                                    <Grid item xs sx={{ textAlign: 'center' }}>
                                        <RespButton
                                            variant="contained"
                                            color="primary"
                                            disabled={!selectedItem || !!selectedItem.locked}
                                            onClick={() => selectedItem && onClickEdit(selectedItem)}
                                            startIcon={<EditIcon />}
                                        >
                                            {i18n('edit')}
                                        </RespButton>
                                    </Grid>

                                    {!ListComponent && (
                                        <>
                                            <If condition={!!allowDuplicate}>
                                                <Grid item xs sx={{ textAlign: 'center' }}>
                                                    <RespButton
                                                        variant="contained"
                                                        color="primary"
                                                        disabled={!selectedItem}
                                                        startIcon={<CallSplitIcon />}
                                                        onClick={() => selectedItem && onClickDuplicate(selectedItem)}
                                                    >
                                                        {i18n('duplicate')}
                                                    </RespButton>
                                                </Grid>
                                            </If>

                                            <Grid item xs sx={{ textAlign: 'center' }}>
                                                <RespButton
                                                    variant="contained"
                                                    color="secondary"
                                                    disabled={!selectedItem || !!selectedItem.locked || !!selectedItem.linked}
                                                    startIcon={<DeleteIcon />}
                                                    onClick={() => selectedItem && onClickDelete(selectedItem)}
                                                >
                                                    {i18n('delete')}
                                                </RespButton>
                                            </Grid>
                                        </>
                                    )}
                                </If>
                            </>
                        )}
                    </GridButtonContainer>
                </Grid>

                <Grid item xs={12} sx={{ textAlign: itemList && (itemList.length || ListComponent) ? 'left' : 'center' }}>
                    {itemList &&
                        (ListComponent ? (
                            <ListComponent
                                itemList={itemList}
                                onChange={setSelectedItem}
                                onView={onClickView}
                                onEdit={onClickEdit}
                                onDelete={onClickDelete}
                                onDuplicate={onClickDuplicate}
                                allowDuplicate={allowDuplicate}
                                userData={userData}
                            />
                        ) : (
                            <ItemListAutocomplete
                                itemList={itemList as unknown as ListItem[]}
                                setSelectedItem={(item) => setSelectedItem(item as unknown as EnrichedItem<T> | null)}
                                selectedItem={selectedItem as unknown as ListItem}
                                onClickEdit={() => selectedItem && onClickEdit(selectedItem)}
                                onClickView={() => selectedItem && onClickView(selectedItem)}
                            />
                        ))}
                </Grid>
            </Grid>
        </Container>
    );
}

export default ItemList;
